import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { optimizedImageProps } from '../utils/imageUrl';
import { ArrowRight, Sparkles, PackageOpen, RotateCcw, ShieldCheck, GitCompareArrows, BookOpen, Search } from 'lucide-react';
import { toast } from 'sonner';
import HomeProductCard from './HomeProductCard';
import { useCart } from '../context/CartContext.jsx';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// ─── Section header used by all 6 sections ───────────────────────────────────
const SectionHeader = ({ icon: Icon, title, subtitle, ctaText, ctaTo, testId }) => (
  <div className="flex justify-between items-end mb-8" data-testid={`${testId}-header`}>
    <div>
      <div className="flex items-center gap-3 mb-3">
        <div
          style={{
            width: 38,
            height: 38,
            borderRadius: 8,
            background: 'rgba(15, 46, 109, 0.08)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'var(--brand-primary)',
          }}
        >
          <Icon size={20} />
        </div>
        <h2 className="hero-medium" style={{ margin: 0, color: 'var(--brand-primary)' }}>{title}</h2>
      </div>
      <p className="body-large" style={{ color: 'var(--text-secondary)' }}>{subtitle}</p>
    </div>
    {ctaText && ctaTo && (
      <Link to={ctaTo} className="btn-icon hidden md:flex" data-testid={`${testId}-cta`}>
        {ctaText} <ArrowRight size={16} />
      </Link>
    )}
  </div>
);

// ─── 1) Most Requested Research Compounds ────────────────────────────────────
const MostRequestedSection = ({ products, featuredCatNos, onAdd }) => {
  // Try data-driven ranking first (engagement events over last 30d).
  // Fall back to admin-curated featured catNos, then to a category heuristic.
  const [dataDriven, setDataDriven] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await fetch(`${API_URL}/api/analytics/most-requested?days=30&limit=4`);
        if (r.ok) {
          const data = await r.json();
          if (!cancelled && data.products && data.products.length >= 2) {
            setDataDriven(data.products);
          }
        }
      } catch (e) {
        // Silent fallback — featured list will be used.
      }
      if (!cancelled) setLoading(false);
    })();
    return () => { cancelled = true; };
  }, []);

  const list = useMemo(() => {
    if (dataDriven && dataDriven.length) return dataDriven.slice(0, 4);
    if (Array.isArray(featuredCatNos) && featuredCatNos.length) {
      return featuredCatNos
        .map((c) => products.find((p) => p.catNo === c))
        .filter(Boolean)
        .slice(0, 4);
    }
    return products
      .filter((p) => p.inStock !== false)
      .filter((p) => ['metabolic', 'tissue', 'tissue-recovery'].includes((p.category || '').toLowerCase()))
      .slice(0, 4);
  }, [dataDriven, products, featuredCatNos]);

  if (loading && !list.length) return null;
  if (!list.length) return null;
  const usingLiveData = !!(dataDriven && dataDriven.length);
  return (
    <section className="section-padding rcm-section reveal" data-testid="most-requested-section">
      <div className="container">
        <SectionHeader
          icon={Sparkles}
          title="Most Requested Research Compounds"
          subtitle={usingLiveData
            ? 'Top 4 by researcher demand over the last 30 days — updated in real time.'
            : 'The peptides researchers most frequently order from the RCM Biosciences catalog this quarter.'}
          ctaText="View all"
          ctaTo="/products"
          testId="most-requested"
        />
        <div className="grid-product-showcase">
          {list.map((p) => <HomeProductCard key={p.id || p.catNo} product={p} onAddToOrder={onAdd} testIdPrefix="most-requested-card" />)}
        </div>
      </div>
    </section>
  );
};

// ─── 2) New to the RCM Catalog ───────────────────────────────────────────────
const NewToCatalogSection = ({ products, onAdd }) => {
  const list = useMemo(() => {
    return [...products]
      .filter((p) => p.created_at)
      .sort((a, b) => String(b.created_at).localeCompare(String(a.created_at)))
      .slice(0, 4);
  }, [products]);
  if (!list.length) return null;
  return (
    <section className="section-padding rcm-section-alt reveal" data-testid="new-to-catalog-section">
      <div className="container">
        <SectionHeader
          icon={PackageOpen}
          title="New to the RCM Catalog"
          subtitle="Recently added research peptides — batch-tested with Certificate of Analysis on arrival."
          ctaText="View catalog"
          ctaTo="/products"
          testId="new-to-catalog"
        />
        <div className="grid-product-showcase">
          {list.map((p) => <HomeProductCard key={p.id || p.catNo} product={p} onAddToOrder={onAdd} testIdPrefix="new-card" />)}
        </div>
      </div>
    </section>
  );
};

// ─── 3) Recently Restocked ───────────────────────────────────────────────────
const RecentlyRestockedSection = ({ products, onAdd }) => {
  const list = useMemo(() => {
    // Heuristic: in-stock + most-recently updated. Real "restocked" requires
    // a stock-history audit log, which is not yet stored. updated_at is a fair
    // proxy because admins typically update_at when they bump stock_count.
    return products
      .filter((p) => p.inStock !== false && p.stock_count !== 0)
      .filter((p) => p.updated_at)
      .sort((a, b) => String(b.updated_at).localeCompare(String(a.updated_at)))
      .slice(0, 4);
  }, [products]);
  if (!list.length) return null;
  return (
    <section className="section-padding rcm-section reveal" data-testid="recently-restocked-section">
      <div className="container">
        <SectionHeader
          icon={RotateCcw}
          title="Recently Restocked"
          subtitle="Back in stock — fresh batches with new Certificates of Analysis."
          ctaText="See in-stock"
          ctaTo="/products"
          testId="recently-restocked"
        />
        <div className="grid-product-showcase">
          {list.map((p) => <HomeProductCard key={p.id || p.catNo} product={p} onAddToOrder={onAdd} testIdPrefix="restocked-card" />)}
        </div>
      </div>
    </section>
  );
};

// ─── 4) Certificate of Analysis Library ─────────────────────────────────────
// Public CoA discovery is product-based, not batch-based (2026-07-23 owner
// spec: our products do not use batch/lot numbers customers can look up).
// This section teases the searchable /coa library and offers a product-name
// search that deep-links straight into the library filter.
const CoALibraryTeaserSection = () => {
  const [q, setQ] = useState('');
  const navigate = useNavigate();

  const submit = (e) => {
    e.preventDefault();
    const t = q.trim();
    navigate(t ? `/coa?q=${encodeURIComponent(t)}` : '/coa');
  };

  return (
    <section className="section-padding rcm-section-alt reveal" data-testid="coa-library-teaser">
      <div className="container">
        <SectionHeader
          icon={ShieldCheck}
          title="Certificate of Analysis Library"
          subtitle="Every RCM Biosciences research peptide ships with an independent third-party CoA. Search the library by product name to view or download the latest report."
          testId="coa-library-teaser"
        />
        <form
          onSubmit={submit}
          data-testid="coa-library-teaser-form"
          style={{
            display: 'flex', gap: '8px', maxWidth: '560px',
            background: 'white', padding: '6px', borderRadius: '8px',
            border: '1px solid rgba(15,46,109,0.15)', flexWrap: 'wrap',
          }}
        >
          <div style={{ flex: 1, display: 'flex', alignItems: 'center', minWidth: '200px' }}>
            <Search size={16} style={{ color: 'var(--text-secondary)', margin: '0 10px' }} />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search by product name (e.g. Tirzepatide, BPC-157)"
              data-testid="coa-library-teaser-input"
              style={{ flex: 1, border: 'none', padding: '10px 0', fontSize: '14px', background: 'transparent', outline: 'none' }}
            />
          </div>
          <button
            type="submit"
            data-testid="coa-library-teaser-submit"
            style={{
              background: 'var(--brand-primary)', color: 'white',
              border: 'none', borderRadius: '4px', padding: '10px 18px',
              fontSize: '13px', fontWeight: 700, cursor: 'pointer',
              letterSpacing: '0.04em',
            }}
          >Browse CoA Library</button>
        </form>
        <p style={{ marginTop: '14px', color: 'var(--text-secondary)', fontSize: '13px', maxWidth: '560px' }}>
          Or open the full <Link to="/coa" style={{ color: 'var(--brand-primary)', textDecoration: 'underline' }}>Certificate of Analysis library</Link> to see every current CoA with testing laboratory, analysis date, and downloadable report.
        </p>
      </div>
    </section>
  );
};


// ─── 5) Research Comparison Center ───────────────────────────────────────────
const ResearchComparisonSection = () => {
  const comparisons = [
    {
      title: 'Semaglutide vs Tirzepatide',
      blurb: 'GLP-1 receptor agonist vs dual GIP/GLP-1 receptor agonist in metabolic research models.',
      href: '/glp-1-research',
      tag: 'GLP-1',
    },
    {
      title: 'BPC-157 vs TB-500',
      blurb: 'Comparing two of the most-studied tissue and recovery research peptides.',
      href: '/tissue-recovery',
      tag: 'Tissue',
    },
    {
      title: 'CJC-1295 With vs Without DAC',
      blurb: 'How the DAC modification changes pharmacokinetics in growth hormone research.',
      href: '/growth-hormone',
      tag: 'GH',
    },
  ];
  return (
    <section className="section-padding rcm-section reveal" data-testid="research-comparison-section">
      <div className="container">
        <SectionHeader
          icon={GitCompareArrows}
          title="Research Comparison Center"
          subtitle="Side-by-side overviews of frequently-compared research peptides for laboratory work."
          ctaText="All articles"
          ctaTo="/blog"
          testId="research-comparison"
        />
        <div className="grid-product-showcase">
          {comparisons.map((c, idx) => (
            <Link
              key={idx}
              to={c.href}
              data-testid={`research-comparison-card-${idx}`}
              className="rcm-card"
              style={{
                padding: '20px',
                display: 'flex',
                flexDirection: 'column',
                gap: '10px',
                textDecoration: 'none',
                color: 'inherit',
                borderLeft: '3px solid var(--brand-primary)',
              }}
            >
              <span style={{
                alignSelf: 'flex-start',
                padding: '3px 9px',
                fontSize: '10px',
                background: 'rgba(15,46,109,0.08)',
                color: 'var(--brand-primary)',
                borderRadius: '10px',
                fontWeight: 700,
                letterSpacing: '0.05em',
              }}>{c.tag}</span>
              <h3 style={{ fontSize: '17px', fontWeight: 700, color: 'var(--brand-primary)', margin: 0 }}>{c.title}</h3>
              <p style={{ fontSize: '13px', color: 'var(--text-secondary)', margin: 0, lineHeight: 1.5 }}>{c.blurb}</p>
              <span style={{ marginTop: 'auto', fontSize: '12px', color: 'var(--brand-primary)', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '4px' }}>
                Read comparison <ArrowRight size={12} />
              </span>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
};

// ─── 6) Latest Research Articles ─────────────────────────────────────────────
const LatestArticlesSection = () => {
  const [articles, setArticles] = useState([]);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await fetch(`${API_URL}/api/blog?limit=3`);
        if (r.ok) {
          const data = await r.json();
          const list = (Array.isArray(data) ? data : data.articles || data.items || [])
            .filter((b) => b.slug && b.published !== false)
            .sort((a, b) => String(b.created_at || '').localeCompare(String(a.created_at || '')))
            .slice(0, 3);
          if (!cancelled) setArticles(list);
        }
      } catch (e) {
        console.error('Failed to load blog articles', e);
      }
    })();
    return () => { cancelled = true; };
  }, []);
  if (!articles.length) return null;
  return (
    <section className="section-padding rcm-section-alt reveal" data-testid="latest-articles-section">
      <div className="container">
        <SectionHeader
          icon={BookOpen}
          title="Latest Research Articles"
          subtitle="Editorial-reviewed coverage of peptide research, laboratory handling, and category overviews."
          ctaText="View blog"
          ctaTo="/blog"
          testId="latest-articles"
        />
        <div className="grid-product-showcase">
          {articles.map((a) => (
            <Link
              key={a.slug}
              to={`/blog/${a.slug}`}
              data-testid={`latest-article-${a.slug}`}
              className="rcm-card"
              style={{
                padding: 0,
                overflow: 'hidden',
                display: 'flex',
                flexDirection: 'column',
                textDecoration: 'none',
                color: 'inherit',
              }}
            >
              {a.featured_image && (
                // onError hides the <img> so a broken URL (e.g. stale
                // customer-assets link returning 403) shows the gradient
                // background instead of the browser's "?" glyph.
                <div style={{
                  height: '160px', overflow: 'hidden',
                  background: 'linear-gradient(135deg, #0A1F44 0%, #0F2E6D 100%)',
                }}>
                  <img
                    {...optimizedImageProps(a.featured_image, { sizes: '(max-width: 768px) 90vw, 360px', maxWidth: 640 })}
                    alt={a.title}
                    width="320"
                    height="160"
                    loading="lazy"
                    decoding="async"
                    style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                    onError={(e) => { e.currentTarget.style.display = 'none'; }}
                  />
                </div>
              )}
              <div style={{ padding: '16px', display: 'flex', flexDirection: 'column', gap: '8px', flex: 1 }}>
                <p style={{ fontSize: '10px', color: 'var(--text-light)', margin: 0, textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 700 }}>
                  {a.created_at ? String(a.created_at).slice(0, 10) : 'Research'}
                </p>
                <h3 style={{ fontSize: '15px', fontWeight: 700, color: 'var(--brand-primary)', margin: 0, lineHeight: 1.35 }}>
                  {a.title}
                </h3>
                {a.excerpt && (
                  <p style={{ fontSize: '13px', color: 'var(--text-secondary)', margin: 0, lineHeight: 1.5 }}>
                    {a.excerpt.slice(0, 130)}…
                  </p>
                )}
                <span style={{ marginTop: 'auto', fontSize: '12px', color: 'var(--brand-primary)', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '4px' }}>
                  Read article <ArrowRight size={12} />
                </span>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
};

// ─── Public container ────────────────────────────────────────────────────────
const HomeNewSections = ({ products, featuredCatNos }) => {
  const { addToCart } = useCart();
  const handleAdd = (product) => {
    if (!product) return;
    addToCart(product, 1);
    toast.success(`${product.name} ${product.dosage || ''} added to your research order`.trim());
  };
  return (
    <>
      <MostRequestedSection products={products} featuredCatNos={featuredCatNos} onAdd={handleAdd} />
      <NewToCatalogSection products={products} onAdd={handleAdd} />
      <RecentlyRestockedSection products={products} onAdd={handleAdd} />
      <CoALibraryTeaserSection />
      <ResearchComparisonSection />
      <LatestArticlesSection />
    </>
  );
};

export default HomeNewSections;
